/**
 * What the Reports screen reads, and the export it writes.
 *
 * Every figure comes from a `security invoker` function, so `customers_read` —
 * and with it `can_see_customer()` — decides what each one covers. A collector
 * opening Reports gets a report of their own accounts; there is no filtering in
 * this file and deleting it would not change that.
 *
 * The export is the one place where "can see" and "may take out of the
 * building" are different rights, and the difference is enforced in
 * `export_customers()` rather than by hiding a button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_FILTERS, ageingWindowRows, numberCell, readAgeingWindows, textCell, dateCell, boolCell, toCsv,
    type AgeingWindows, type BookFilters, type ReportPeriod, type TenantContext,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { filterArgs } from '../book/useBook';
import { readAgeingReport, type AgeingReportRow } from '../receivables/ageingReport';

/* ------------------------------ what it reads ----------------------------- */

/** The current position. Not a claim about any period. */
export interface Receivables {
    outstanding: number;
    receivable: number;
    creditAmount: number;
    creditCount: number;
    withDuesCount: number;
    settledCount: number;
    overdueAmount: number;
    overdueCount: number;
    dueTodayCount: number;
    noFollowUpCount: number;
    urgentCount: number;
    badDebtAmount: number;
    badDebtCount: number;
    promisedAmount: number;
    promisedCount: number;
    /** Owing accounts not marked as bad debt — the base follow-up coverage is measured over (0033). */
    planBaseCount: number;
    /** Of those, the ones with a plan: due today, scheduled, or marked collected. */
    plannedCount: number;
}

/** One row of the ageing report: a sheet's band, or a TallyPrime company's own window and what it leaves beside it (D3). */
export type AgeingRow = AgeingReportRow;

/** What people did between two dates. */
export interface ActivityTotals {
    followUps: number;
    accountsTouched: number;
    promises: number;
    promisedAmount: number;
    callsAnswered: number;
    callsUnanswered: number;
    visits: number;
    disputes: number;
    paymentsLogged: number;
    chequesCleared: number;
    chequesClearedAmount: number;
    accountsSettled: number;
}

export interface MemberReport {
    memberId: string;
    displayName: string;
    assignedCount: number;
    assignedAmount: number;
    overdueCount: number;
    overdueAmount: number;
    noFollowUpCount: number;
    followUps: number;
    promises: number;
    promisedAmount: number;
    /** In `assignedCount` but out of the queue (0033): outside overdue, not set and coverage. */
    writtenOffCount: number;
}

/**
 * Promises somebody answered in the period (`report_promises`, 0033). The
 * amounts are what was promised — the product does not see the bank.
 */
export interface PromiseOutcomes {
    keptCount: number;
    keptAmount: number;
    notKeptCount: number;
    notKeptAmount: number;
}

export interface ReportData {
    receivables: Receivables | null;
    ageing: AgeingRow[];
    activity: ActivityTotals | null;
    promises: PromiseOutcomes | null;
    /** Open promises whose day has gone and nobody has answered — the list's "Promise date passed", now. */
    promisesAwaiting: { count: number; amount: number } | null;
    team: MemberReport[];
    /** True when the team figures were refused rather than merely empty. */
    teamUnavailable: boolean;
}

const EMPTY: ReportData = {
    receivables: null, ageing: [], activity: null, promises: null, promisesAwaiting: null, team: [], teamUnavailable: false,
};

const num = (value: unknown): number => Number(value) || 0;

export function useReports(
    context: TenantContext,
    period: ReportPeriod,
    options: { withTeam: boolean; owner?: string },
) {
    const workspaceId = context.workspace.id;
    const [data, setData] = useState<ReportData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { withTeam } = options;
    // Whose accounts the report covers: 'any' is everything this person may see (0033).
    const owner = options.owner || 'any';
    const key = JSON.stringify([workspaceId, period.from, period.to, withTeam, owner]);
    const issuedFor = useRef(key);

    const load = useCallback(async () => {
        issuedFor.current = key;
        setLoading(true);
        setError(null);

        const client = supabase();
        const dates = { p_workspace: workspaceId, p_from: period.from, p_to: period.to };

        const [receivables, ageing, activity, promises, awaiting, team] = await Promise.all([
            client.rpc('report_receivables', { p_workspace: workspaceId, p_owner: owner }),
            client.rpc('report_ageing', { p_workspace: workspaceId, p_owner: owner, p_windows: true }),
            client.rpc('report_activity', { ...dates, p_owner: owner }),
            client.rpc('report_promises', { ...dates, p_owner: owner }),
            // The same question the list's "Promise date passed" asks, so the
            // figure and the list it opens cannot disagree.
            client.rpc('book_totals', filterArgs(workspaceId, {
                ...DEFAULT_FILTERS, followUp: 'promise_passed', owner,
            })),
            withTeam ? client.rpc('report_by_member', dates) : Promise.resolve({ data: null, error: null }),
        ]);

        // An answer that arrives after the period moved on describes a question
        // nobody is asking any more.
        if (issuedFor.current !== key) return;

        if (receivables.error) {
            setError('We could not build your report. Try again in a moment.');
            setLoading(false);
            return;
        }

        const one = (result: { data: unknown }): Record<string, unknown> | null => {
            const row = Array.isArray(result.data) ? result.data[0] : result.data;
            return (row as Record<string, unknown> | undefined) ?? null;
        };

        const r = one(receivables);
        const a = one(activity);
        const p = one(promises);
        const w = one(awaiting);

        setData({
            receivables: r ? {
                outstanding: num(r['outstanding']),
                receivable: num(r['receivable']),
                creditAmount: num(r['credit_amount']),
                creditCount: num(r['credit_count']),
                withDuesCount: num(r['with_dues_count']),
                settledCount: num(r['settled_count']),
                overdueAmount: num(r['overdue_amount']),
                overdueCount: num(r['overdue_count']),
                dueTodayCount: num(r['due_today_count']),
                noFollowUpCount: num(r['no_follow_up_count']),
                urgentCount: num(r['urgent_count']),
                badDebtAmount: num(r['bad_debt_amount']),
                badDebtCount: num(r['bad_debt_count']),
                promisedAmount: num(r['promised_amount']),
                promisedCount: num(r['promised_count']),
                planBaseCount: num(r['plan_base_count']),
                plannedCount: num(r['planned_count']),
            } : null,
            ageing: readAgeingReport((ageing.data ?? []) as Record<string, unknown>[]),
            activity: a && !activity.error ? {
                followUps: num(a['follow_ups']),
                accountsTouched: num(a['accounts_touched']),
                promises: num(a['promises']),
                promisedAmount: num(a['promised_amount']),
                callsAnswered: num(a['calls_answered']),
                callsUnanswered: num(a['calls_unanswered']),
                visits: num(a['visits']),
                disputes: num(a['disputes']),
                paymentsLogged: num(a['payments_logged']),
                chequesCleared: num(a['cheques_cleared']),
                chequesClearedAmount: num(a['cheques_cleared_amount']),
                accountsSettled: num(a['accounts_settled']),
            } : null,
            promises: p && !promises.error ? {
                keptCount: num(p['kept_count']),
                keptAmount: num(p['kept_amount']),
                notKeptCount: num(p['not_kept_count']),
                notKeptAmount: num(p['not_kept_amount']),
            } : null,
            promisesAwaiting: w && !awaiting.error ? { count: num(w['matched_count']), amount: num(w['outstanding']) } : null,
            team: ((team.data ?? []) as Record<string, unknown>[]).map(row => ({
                memberId: String(row['member_id']),
                displayName: String(row['display_name'] ?? 'Somebody'),
                assignedCount: num(row['assigned_count']),
                assignedAmount: num(row['assigned_amount']),
                overdueCount: num(row['overdue_count']),
                overdueAmount: num(row['overdue_amount']),
                noFollowUpCount: num(row['no_follow_up_count']),
                followUps: num(row['follow_ups']),
                promises: num(row['promises']),
                promisedAmount: num(row['promised_amount']),
                writtenOffCount: num(row['written_off_count']),
            })),
            teamUnavailable: withTeam && Boolean(team.error),
        });
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => { void load(); }, [load]);

    return { ...data, loading, error, reload: load };
}

/* -------------------------------- the export ------------------------------ */

/**
 * How many accounts one export may carry.
 *
 * Bounded on purpose. The alternative is an export that works until the day a
 * company's book grows past whatever the browser can hold, and then fails in
 * the middle of somebody's month end. When the cap is reached the file is still
 * produced and the screen says plainly what it contains.
 *
 * **Few, large calls.** Every call filters and sorts the whole book before its
 * offset applies, so the cost of a call is the cost of the book, whatever the
 * page size. Since 0024 that is one pass through Row Level Security per account
 * — about a quarter of a second for 5,008 accounts locally, the same at offset
 * 0 and offset 4,000 — and paging small would only multiply it.
 */
export const EXPORT_LIMIT = 10_000;

/**
 * One call's worth, and it is **PostgREST's own ceiling** rather than a choice.
 *
 * PostgREST returns at most `max_rows` and truncates silently. Asking for more
 * would hand this loop a short page it would read as "the end", and the file
 * would quietly be missing most of the company. The database function caps at
 * the same number so the two cannot drift apart.
 */
const EXPORT_PAGE = 1_000;

export interface ExportRow {
    company: string;
    source_key: string;
    contact_person: string;
    contact_number: string;
    email: string;
    city: string;
    state: string;
    category: string;
    outstanding: number;
    direction: string;
    bucket_1_45: number;
    bucket_46_90: number;
    bucket_91_135: number;
    bucket_over_135: number;
    overdue_45: number;
    overdue_90: number;
    follow_up_state: string;
    follow_up_on: string | null;
    payment_rank: string;
    is_urgent: boolean;
    assigned_to: string;
    last_contacted: string | null;
    /** A TallyPrime account's own windows (0036); absent on a sheet's accounts and on an older database. */
    ageing_windows?: unknown;
}

export class ExportError extends Error {
    constructor(message: string, readonly kind: 'denied' | 'failed') {
        super(message);
    }
}

const FOLLOW_UP_WORDS: Record<string, string> = {
    today: 'Due today', overdue: 'Overdue', future: 'Scheduled',
    no_follow_up: 'None set', completed: 'Collected',
};

/**
 * The columns an export carries.
 *
 * No ids — a `uuid` is meaningless in a spreadsheet and is the kind of thing
 * that ends up pasted into a support ticket. No secrets, no audit internals, no
 * internal flags. Amounts are numbers so they add up; everything else is text
 * and is protected against being read as a formula.
 *
 * The currency is named once, in the heading, because a spreadsheet cannot add
 * up a column of "£1,234.00".
 */
export const exportColumns = (currency: string, rows: readonly ExportRow[] = []) => [
    ...BOOK_COLUMNS(currency),
    ...windowColumns(currency, rows),
];

/**
 * A TallyPrime company's own windows, when its accounts carry them (D3,
 * docs/tally/18 §1.3): one column each, named as Settings names them, then what
 * they leave beside them. After every column the file always had, so a
 * spreadsheet built on it keeps working. A row without the same windows (a
 * sheet's account, one settled since) leaves them empty: absent, not nought.
 */
function windowColumns(currency: string, rows: readonly ExportRow[]) {
    const windowsOf = (r: ExportRow) => readAgeingWindows({ windows: r.ageing_windows });
    const layout = rows.map(windowsOf).find((w): w is AgeingWindows => w !== null);
    if (!layout) return [];
    const cellOf = (pick: (w: AgeingWindows) => number) => (r: ExportRow) => {
        const w = windowsOf(r);
        return w && w.basis === layout.basis && w.boundaries.join(',') === layout.boundaries.join(',') ? numberCell(pick(w)) : '';
    };
    return [
        ...ageingWindowRows(layout).map(({ label }, i) => ({ header: `${label} (${currency})`, cell: cellOf(w => w.amounts[i] ?? 0) })),
        ...(layout.basis === 'due_date' ? [{ header: `Not yet due (${currency})`, cell: cellOf(w => w.notYetDue) }] : []),
        { header: `Not in any bucket (${currency})`, cell: cellOf(w => w.notAged) },
        { header: `Credits, against no bill yet (${currency})`, cell: cellOf(w => w.credits) },
    ];
}

const BOOK_COLUMNS = (currency: string) => [
    { header: 'Customer', cell: (r: ExportRow) => textCell(r.company) },
    { header: 'Code', cell: (r: ExportRow) => textCell(r.source_key) },
    { header: 'Contact', cell: (r: ExportRow) => textCell(r.contact_person) },
    { header: 'Phone', cell: (r: ExportRow) => textCell(r.contact_number) },
    { header: 'Email', cell: (r: ExportRow) => textCell(r.email) },
    { header: 'City', cell: (r: ExportRow) => textCell(r.city) },
    { header: 'State', cell: (r: ExportRow) => textCell(r.state) },
    { header: 'Business type', cell: (r: ExportRow) => textCell(r.category) },
    { header: `Outstanding (${currency})`, cell: (r: ExportRow) => numberCell(r.outstanding) },
    { header: 'Debit or credit', cell: (r: ExportRow) => textCell(r.direction) },
    { header: `1-45 days (${currency})`, cell: (r: ExportRow) => numberCell(r.bucket_1_45) },
    { header: `46-90 days (${currency})`, cell: (r: ExportRow) => numberCell(r.bucket_46_90) },
    { header: `91-135 days (${currency})`, cell: (r: ExportRow) => numberCell(r.bucket_91_135) },
    { header: `Over 135 days (${currency})`, cell: (r: ExportRow) => numberCell(r.bucket_over_135) },
    { header: `Overdue over 45 (${currency})`, cell: (r: ExportRow) => numberCell(r.overdue_45) },
    { header: `Overdue over 90 (${currency})`, cell: (r: ExportRow) => numberCell(r.overdue_90) },
    { header: 'Follow-up', cell: (r: ExportRow) => textCell(FOLLOW_UP_WORDS[r.follow_up_state] ?? r.follow_up_state) },
    { header: 'Follow-up date', cell: (r: ExportRow) => dateCell(r.follow_up_on) },
    { header: 'Rank', cell: (r: ExportRow) => textCell(r.payment_rank) },
    { header: 'Urgent', cell: (r: ExportRow) => boolCell(r.is_urgent) },
    { header: 'Assigned to', cell: (r: ExportRow) => textCell(r.assigned_to) },
    { header: 'Last contacted', cell: (r: ExportRow) => dateCell(r.last_contacted) },
];

export interface ExportResult {
    csv: string;
    rowCount: number;
    /** True when the cap stopped it, so the screen can say so. */
    capped: boolean;
}

/** What `book_totals()` says the same filters match. */
export interface BookFigures {
    matchedCount: number;
    outstanding: number;
}

/**
 * Half a cent. The file's total is added up here from amounts that crossed
 * JSON as floating point, the Book's in Postgres `numeric`; compensated
 * summation keeps the difference far below this for any real book, and one
 * account missing or doubled moves it by that account's balance.
 */
const TOTAL_TOLERANCE = 0.005;

/** Netted like the Book's own figure: a credit is money held, not owed. */
function nettedTotal(rows: ExportRow[]): number {
    // Neumaier's compensated sum, so ten thousand balances add up to what
    // Postgres gets rather than to what a naive float loop drifts to.
    let sum = 0;
    let carry = 0;
    for (const row of rows) {
        const amount = Number(row.outstanding) || 0;
        const value = row.direction === 'Credit' ? -amount : amount;
        const next = sum + value;
        carry += Math.abs(sum) >= Math.abs(value) ? (sum - next) + value : (value - next) + sum;
        sum = next;
    }
    return sum + carry;
}

/**
 * Does the assembled file say what the Book says?
 *
 * `complete` — every matching account is in it, once: the count and the netted
 * total both agree. `capped` — the cap stopped it and the Book really does have
 * more (the file is the largest `EXPORT_LIMIT`, which is all it claims to be).
 * `mismatch` — anything else, and the file must not be handed over.
 */
export function checkExportAgainstBook(
    rows: ExportRow[], book: BookFigures,
): 'complete' | 'capped' | 'mismatch' {
    if (rows.length >= EXPORT_LIMIT && book.matchedCount > rows.length) return 'capped';
    if (rows.length !== book.matchedCount) return 'mismatch';
    return Math.abs(nettedTotal(rows) - book.outstanding) <= TOTAL_TOLERANCE ? 'complete' : 'mismatch';
}

const exportRefused = (message: string): ExportError =>
    /cannot export|insufficient|permission denied/i.test(message)
        ? new ExportError(
            'You can see this report but not take it out of the product. '
            + 'Somebody who manages roles can grant that.',
            'denied',
        )
        : new ExportError('We could not build that export. Try again in a moment.', 'failed');

/** Every page of the export, until a short one says it is the end. */
async function readExportPages(
    workspaceId: string,
    filters: BookFilters,
    ids: readonly string[] | null,
    onProgress?: (rows: number) => void,
): Promise<ExportRow[]> {
    const client = supabase();
    const rows: ExportRow[] = [];

    for (let offset = 0; offset < EXPORT_LIMIT; offset += EXPORT_PAGE) {
        const { data, error } = await client.rpc('export_customers', {
            ...filterArgs(workspaceId, filters),
            p_ids: ids,
            p_sort: 'amount-desc',
            p_limit: EXPORT_PAGE,
            p_offset: offset,
        });
        if (error) throw exportRefused(error.message);

        const page = (data ?? []) as ExportRow[];
        rows.push(...page);
        onProgress?.(rows.length);
        // A short page is the end — and it can only mean that, because the
        // page size is the transport's own ceiling. The check afterwards is
        // what makes that more than a belief.
        if (page.length < EXPORT_PAGE) break;
    }
    return rows;
}

/** The Book's own count and total for the same question. */
async function readBookFigures(workspaceId: string, filters: BookFilters, ids: readonly string[] | null): Promise<BookFigures> {
    const { data, error } = await supabase().rpc('book_totals', { ...filterArgs(workspaceId, filters), p_ids: ids });
    const row = (Array.isArray(data) ? data[0] : data) as
        { matched_count?: number | string; outstanding?: number | string } | null;
    if (error || !row) {
        throw new ExportError('We could not check that export against your customer list. Try again in a moment.', 'failed');
    }
    return { matchedCount: Number(row.matched_count), outstanding: Number(row.outstanding) };
}

/**
 * Build the file, a page at a time, from the caller's own authorised rows.
 *
 * **Filters go to the server; rows come back.** The browser never sends a list
 * of customer ids to be exported — it sends the same question the screen was
 * showing, and `export_customers()` answers it under the caller's own
 * authorisation. An id supplied by hand would reach nothing it could not
 * already read, and there is no code path here that would carry one.
 *
 * **Then it is checked against the Book.** A file assembled from several calls
 * can be wrong in ways no single call can see: a transport that cuts a page
 * short makes it look like the last one, and a sync that settles an account or
 * moves a balance between two pages shifts every later row, so one account
 * lands in two pages and another in none. So the same filters go to
 * `book_totals()`, and the file's count and netted total must be the Book's.
 * If they are not, the file is built once more; if it still does not add up,
 * nothing is downloaded and the reader is told why.
 */
export async function exportCustomers(
    workspaceId: string,
    filters: BookFilters,
    currency: string,
    onProgress?: (rows: number) => void,
    /**
     * A selection (0033): only these accounts, and only those the filters and
     * the caller's scope also allow — ids narrow, they never widen.
     */
    ids: readonly string[] | null = null,
): Promise<ExportResult> {
    for (let attempt = 1; ; attempt += 1) {
        const rows = await readExportPages(workspaceId, filters, ids, onProgress);
        const verdict = checkExportAgainstBook(rows, await readBookFigures(workspaceId, filters, ids));

        if (verdict !== 'mismatch') {
            return {
                csv: toCsv(exportColumns(currency, rows), rows),
                rowCount: rows.length,
                capped: verdict === 'capped',
            };
        }
        if (attempt >= 2) {
            throw new ExportError(
                'The accounts changed while the file was being built, so it did not add up to your customer list. '
                + 'Nothing was downloaded. Try again in a moment.',
                'failed',
            );
        }
    }
}

/**
 * Hand the file to the browser.
 *
 * A blob and an object URL, revoked immediately after: a data URL would put the
 * whole export in the address bar, which several browsers truncate without
 * saying so.
 */
export function downloadCsv(fileName: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
